import { WorkflowRunStatus } from "pages/approvals/workflows/workflow-run-status";
import { useVisiblePostStatuses } from "hooks/use-visible-post-statuses";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { changeCollabState } from "actions/collaboration";
import {
  getPage,
  getRoutinesLoading,
  getUser,
} from "selectors/commonSelectors";
import { Icon } from "@dialogueconsulting/sked-ui";

import { PAGES } from "constants/Pages";

import {
  BULK_CHANGE_COLLAB_STATE_ROUTINE,
  CHANGE_COLLAB_STATE_ROUTINE,
} from "constants/ActionTypes";
import { getFilters } from "selectors/postsPageSelectors";
import { filterCollabStatus } from "actions/postsView";

import { Post } from "features/post";
import { FormSize, FormThemeProvider } from "ui/ecosystems/forms";
import { Tooltip, TooltipTrigger, TooltipContent } from "components/ui/tooltip";
import { ReduxRootState } from "../../../types/redux-store";

import { resolveDefaultAprovalStatus } from "./status-change.utils";
import {
  canMovePostToStatus,
  canChangeToStatus,
  STATUS_CHANGE_NOT_ALLOWED_TOOLTIP,
  STATUS_CHANGE_UPSELL_TOOLTIP,
} from "./status-change-permissions";
import { cn } from "@/lib/utils";
import {
  findEffectivePostStatus,
  hasEffectiveBasicPostStatusProjection,
  isReadOnlyPostStatusCatalog,
} from "utils/post-statuses";

const CLEAR_STATUS_VALUE = "__clear_collab_status__";

interface Option {
  label: string;
  value: string;
  color?: string;
  numberSelected?: number;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

interface Props {
  post?: Post;
  disabled?: boolean;
  numberSelected?: number;
  placeholder?: string;
  selectDefault?: boolean;
  isClearable?: boolean;
  isAbleToUpdateState?: boolean;
  originalPostState?: string;
  postType?: string;

  onChange(value: string): void;

  onMenuOpen?: () => void;
  selectedStatusKey?: string;
  additionalOptions?: Option[];
  size?: FormSize;
  triggerClassName?: string;
  variant?: "default" | "compact";
  onSuccess?: (value: string) => void;
}

export const StatusChange = ({
  post,
  onChange,
  disabled = false,
  numberSelected = 0,
  placeholder = "",
  selectDefault = true,
  isClearable = false,
  postType,
  onMenuOpen,
  selectedStatusKey,
  additionalOptions,
  size = FormSize.default,
  triggerClassName,
  variant = "default",
  onSuccess,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const page = useSelector(getPage);
  const isLoadingRoutines = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [
      BULK_CHANGE_COLLAB_STATE_ROUTINE,
      CHANGE_COLLAB_STATE_ROUTINE,
    ])
  );

  const { postStatusKey } = useSelector(getFilters);
  const userCollabSettings = useVisiblePostStatuses(
    post?.accountIds?.length
      ? post.accountIds
      : post?.accountId
        ? [post.accountId]
        : undefined,
    [selectedStatusKey || post?.postStatusKey || ""]
  );

  const { defaultSetting, upsell } = resolveDefaultAprovalStatus(
    user,
    userCollabSettings
  );
  const isExistingPost = !!post?._id && !post.newPost;
  const effectivePostStatusKey =
    findEffectivePostStatus(userCollabSettings, post?.postStatusKey)?.key ??
    defaultSetting?.key;

  const options = useMemo(() => {
    const userOptions = userCollabSettings.map((setting) => {
      // Mirror the collaboration restriction pattern: keep the select itself
      // enabled and disable individual options instead. When the post-statuses
      // feature is unavailable (upsell), only non-publishable statuses are
      // disabled — publishable ones (e.g. "Ready to publish") stay selectable
      // so a user can still move a post to a publishable state (e.g. after a
      // downgrade) rather than being stuck in "Ready to review".
      const isCurrentStatus =
        isExistingPost && setting.key === effectivePostStatusKey;
      const isRestrictedByCollab = isCurrentStatus
        ? false
        : isExistingPost
          ? !canMovePostToStatus(user, effectivePostStatusKey, setting.key)
          : !canChangeToStatus(user, setting.key);
      const isRestrictedByPlan = upsell && !setting.isPublishingAllowed;
      const isDisabled =
        isRestrictedByCollab ||
        isRestrictedByPlan ||
        isReadOnlyPostStatusCatalog(userCollabSettings);

      return {
        label: setting.label,
        value: setting.key,
        color: setting.color,
        numberSelected,
        isDisabled,
        disabledTooltip: isRestrictedByPlan
          ? STATUS_CHANGE_UPSELL_TOOLTIP
          : isRestrictedByCollab
            ? STATUS_CHANGE_NOT_ALLOWED_TOOLTIP
            : undefined,
      };
    });

    return additionalOptions
      ? [...userOptions, ...additionalOptions]
      : userOptions;
  }, [
    numberSelected,
    isExistingPost,
    effectivePostStatusKey,
    user,
    userCollabSettings,
    additionalOptions,
    upsell,
  ]);

  const [currentSelectedStatus, setCurrentSelectedStatus] = useState(
    selectDefault
      ? selectedStatusKey ||
          effectivePostStatusKey ||
          defaultSetting?.key ||
          options.find((o) => !o.isDisabled)?.value
      : null
  );

  // Stable ref for onChange to avoid re-firing when parent passes a new function reference
  const onChangeRef = useRef(onChange);
  // eslint-disable-next-line react-hooks/refs -- The auto-notify effect reads this ref; update during render to avoid one-render-stale callbacks without adding onChange to the effect deps.
  onChangeRef.current = onChange;

  // Track last auto-notified value to prevent re-notification loops
  const lastAutoNotifiedRef = useRef<string | null>(null);

  // Sync from prop and auto-select defaults, notifying parent when needed
  useEffect(() => {
    if (isReadOnlyPostStatusCatalog(userCollabSettings)) {
      setCurrentSelectedStatus(effectivePostStatusKey ?? null);
      return;
    }
    if (selectDefault && !selectedStatusKey) {
      // No explicit value from parent — auto-select (post status > default) and notify
      let autoSelected =
        effectivePostStatusKey ||
        defaultSetting?.key ||
        options.find((o) => !o.isDisabled)?.value;
      if (hasEffectiveBasicPostStatusProjection(userCollabSettings)) {
        if (
          (isExistingPost || post?.postStatusKey) &&
          autoSelected !== post?.postStatusKey
        ) {
          // A display alias is not a user-authorized edit. In particular, do
          // not copy it into a full-object save through the form callback.
          setCurrentSelectedStatus(autoSelected ?? null);
          return;
        }
        if (
          !post?.postStatusKey &&
          autoSelected &&
          !canChangeToStatus(user, autoSelected)
        ) {
          autoSelected = options.find(
            (option) =>
              !option.isDisabled && canChangeToStatus(user, option.value)
          )?.value;
          if (!autoSelected) {
            setCurrentSelectedStatus(null);
            return;
          }
        }
      }
      if (autoSelected && autoSelected !== lastAutoNotifiedRef.current) {
        lastAutoNotifiedRef.current = autoSelected;
        setCurrentSelectedStatus(autoSelected);
        onChangeRef.current(autoSelected);
      }
    } else if (selectedStatusKey) {
      // Parent provided an explicit value — sync to it
      setCurrentSelectedStatus(
        findEffectivePostStatus(userCollabSettings, selectedStatusKey)?.key ??
          selectedStatusKey
      );
      // Reset auto-notify guard so that if the parent later clears the value,
      // the auto-select branch can re-notify with the default
      lastAutoNotifiedRef.current = null;
    } else {
      // selectDefault=false and selectedStatusKey is empty — clear the selection
      setCurrentSelectedStatus(null);
      // Reset guard so a future link with empty status gets a fresh auto-notification
      lastAutoNotifiedRef.current = null;
    }
  }, [
    selectedStatusKey,
    selectDefault,
    defaultSetting?.key,
    effectivePostStatusKey,
    userCollabSettings,
    user,
    isExistingPost,
    post?.postStatusKey,
    options,
  ]);

  const getResolvedStatusId = (statusId: string) =>
    statusId === CLEAR_STATUS_VALUE ? "" : statusId;

  const onStatusOnChange = (statusId: string) => {
    const resolvedStatusId = getResolvedStatusId(statusId);

    if (
      (page === PAGES.DRAFT || page === PAGES.UPCOMING) &&
      post?._id &&
      !post?.newPost
    ) {
      dispatch(
        filterCollabStatus(postStatusKey?.length ? [...postStatusKey] : [])
      );
    }

    setCurrentSelectedStatus(resolvedStatusId || null);
    onChange(resolvedStatusId);
  };

  const onStatusChange = async (statusId: string) => {
    const resolvedStatusId = getResolvedStatusId(statusId);
    if (isReadOnlyPostStatusCatalog(userCollabSettings)) return;

    if (!resolvedStatusId && isClearable) {
      // Clearing is currently a filter-only affordance. Post-backed status
      // changes must dispatch to the backend before local state is cleared.
      if (post?._id && !post.newPost) {
        toast.error("Clearing post approval status is not supported yet.");
        return;
      }

      onStatusOnChange(resolvedStatusId);
      return;
    }

    if (!resolvedStatusId || resolvedStatusId === post?.postStatusKey) {
      return;
    }

    if (
      isExistingPost &&
      !canMovePostToStatus(user, effectivePostStatusKey, resolvedStatusId)
    ) {
      toast.error(STATUS_CHANGE_NOT_ALLOWED_TOOLTIP);
      return;
    }

    let postStatusType = postType;

    // only perform these actions if page and post are provided
    // and post has other object other than postStatusKey
    if (!post || post?.newPost) {
      onStatusOnChange(resolvedStatusId);
    } else if (post) {
      try {
        // if no page is passed in use whether the post has a date prop of some kind to determine status type
        const { nextRunAt, when, scheduledFor, postType } = post;
        const possibleScheduledPost = nextRunAt || when || scheduledFor;
        const guessedPostType = possibleScheduledPost ? "schedule" : "draft";
        postStatusType = postStatusType || postType || guessedPostType;

        // do dispatch first before changing the dropdown in case it fails
        await dispatch(
          changeCollabState(
            [post._id],
            postStatusType,
            resolvedStatusId,
            () => {
              onStatusOnChange(resolvedStatusId);
              onSuccess?.(resolvedStatusId);
            }
          )
        );
      } catch (error) {
        console.error("Failed to change post status:", error);
        toast.error("Failed to update post status. Please try again.");
      }
    }
  };

  const selectedOption = options.find(
    (opt) => opt.value === currentSelectedStatus
  );

  const selectEl = (
    <Select
      disabled={
        isLoadingRoutines ||
        disabled ||
        isReadOnlyPostStatusCatalog(userCollabSettings)
      }
      value={currentSelectedStatus || undefined}
      onValueChange={onStatusChange}
      onOpenChange={(open) => {
        if (open && onMenuOpen) {
          onMenuOpen();
        }
      }}
    >
      <SelectTrigger
        className={cn(
          "h-9 min-w-[250px] bg-white text-sm font-light focus:border-blue-300 focus:ring-blue-500/10",
          variant === "compact" &&
            "h-5 w-auto min-w-0 max-w-full gap-1.5 rounded-[4px] px-1.5 py-0 text-xs font-medium leading-4 shadow-none [&>span]:min-w-0 [&>i]:size-3 [&>i]:text-[10px] [&>i]:leading-3",
          triggerClassName
        )}
        title={variant === "compact" ? selectedOption?.label : undefined}
      >
        <SelectValue placeholder={placeholder || "Select state"}>
          {selectedOption && (
            <div
              className={cn(
                "flex items-center gap-2",
                variant === "compact" && "min-w-0 gap-1.5"
              )}
            >
              <div
                className={cn(
                  "h-2 w-2 rounded-full flex-shrink-0",
                  variant === "compact" && "size-3"
                )}
                style={{
                  backgroundColor: selectedOption.color || "#9CA3AF",
                }}
              />
              <span
                className={cn(
                  "font-inter text-sm font-light",
                  variant === "compact" &&
                    "min-w-0 truncate text-xs font-medium"
                )}
              >
                {selectedOption.label}
              </span>
              {upsell && <Icon name="upsell-rocket-blue-bg" size="md" />}
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {isClearable && !post && !selectDefault && currentSelectedStatus && (
          <SelectItem value={CLEAR_STATUS_VALUE} className="cursor-pointer">
            <div className="flex items-center gap-2">
              <i
                className="fa-regular fa-circle-xmark text-neutral-400"
                aria-hidden="true"
              />
              <span className="font-inter font-normal">Clear status</span>
            </div>
          </SelectItem>
        )}
        {options.map((option) =>
          option.disabledTooltip ? (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <div>
                  <SelectItem
                    value={option.value}
                    disabled={option.isDisabled}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-2 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: option.color || "#9CA3AF",
                        }}
                      />
                      <span className="font-inter font-normal">
                        {option.label}
                      </span>
                    </div>
                  </SelectItem>
                </div>
              </TooltipTrigger>
              <TooltipContent>{option.disabledTooltip}</TooltipContent>
            </Tooltip>
          ) : (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.isDisabled}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: option.color || "#9CA3AF" }}
                />
                <span className="font-inter font-normal">{option.label}</span>
              </div>
            </SelectItem>
          )
        )}
      </SelectContent>
    </Select>
  );

  return (
    <FormThemeProvider size={size}>
      {upsell ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{selectEl}</div>
          </TooltipTrigger>
          <TooltipContent>Upgrade to access approval statuses</TooltipContent>
        </Tooltip>
      ) : (
        selectEl
      )}
      {post?._id && !post.newPost && (
        <WorkflowRunStatus postId={post._id} controls={variant !== "compact"} />
      )}
    </FormThemeProvider>
  );
};

export default StatusChange;
